import { useEffect, useId, useState } from "react";
import { ThumbsDown, ThumbsUp, CircleCheck } from "lucide-react";
import { submitFeedback } from "@/api/rca-api.js";
import { Button } from "@/components/ui/button.jsx";
import { LoadingSpinner } from "@/components/ui/loading.jsx";
import { cn } from "@/lib/utils.js";
import { PENDO_IGNORE_CLASS } from "@/lib/pendo-targets.js";

const MAX_REMARKS_LENGTH = 500;

const HELPFUL_CHOICES = [
  "Accurate diagnosis",
  "Actionable recommendations",
  "Good use of alarm data",
  "Clear explanation",
  "Correctly identified pattern",
];

const NOT_HELPFUL_CHOICES = [
  "Incorrect diagnosis",
  "Too generic / vague",
  "Missed the root cause",
  "Wrong terminology",
  "Recommendations not practical",
  "Didn't use sensor data well",
  "Hallucinated information",
];

/**
 * Renders a feedback bubble from the pre-defined choices.
 * @param {{
 *   label: string,
 *   selected: boolean,
 *   tone: "helpful" | "not-helpful",
 *   onToggle: () => void,
 *   disabled?: boolean,
 * }} props
 */
function FeedbackChoiceBubble({
  label,
  selected,
  tone,
  onToggle,
  disabled = false,
}) {
  const isHelpful = tone === "helpful";

  return (
    <button
      type='button'
      disabled={disabled}
      aria-pressed={selected}
      onClick={(e) => {
        e.stopPropagation(); // Pendo is latching on to these feedback buttons because it thinks it should. Make double sure it can't.
        onToggle();
      }}
      className={cn(
        // Without PENDO_IGNORE_CLASS, Pendo latches onto this feedback UI.
        PENDO_IGNORE_CLASS,
        "rounded-full border px-3 py-1.5 text-xs font-medium transition-colors",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-offset-background",
        isHelpful
          ? "focus-visible:ring-green-500/60"
          : "focus-visible:ring-red-500/60",
        selected
          ? isHelpful
            ? "border-green-500 bg-green-500/20 text-green-800 shadow-sm dark:text-green-200"
            : "border-red-500 bg-red-500/20 text-red-800 shadow-sm dark:text-red-200"
          : "border-border bg-card text-foreground hover:border-border hover:bg-muted",
        disabled && "cursor-not-allowed opacity-50"
      )}>
      {label}
    </button>
  );
}

/**
 * @param {{ analysisRunId: string }} props
 */
export default function RcaAiAnalysisFeedback({ analysisRunId }) {
  const [wasHelpful, setWasHelpful] = useState(
    /** @type {boolean|null} */ (null)
  );
  const [selectedChoices, setSelectedChoices] = useState(
    /** @type {string[]} */ ([])
  );
  const [remarks, setRemarks] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(/** @type {string|null} */ (null));
  const [submitted, setSubmitted] = useState(false);
  const remarksId = useId();
  const choicesGroupId = useId();

  useEffect(() => {
    setWasHelpful(null);
    setSelectedChoices([]);
    setRemarks("");
    setIsSubmitting(false);
    setError(null);
    setSubmitted(false);
  }, [analysisRunId]);

  /**
   * @param {boolean} helpful
   */
  function selectHelpfulness(helpful) {
    setWasHelpful(helpful);
    setSelectedChoices([]);
    setError(null);
  }

  /**
   * @param {string} choice
   */
  function toggleChoice(choice) {
    setSelectedChoices((prev) =>
      prev.includes(choice)
        ? prev.filter((item) => item !== choice)
        : [...prev, choice]
    );
  }

  async function handleSubmit() {
    if (wasHelpful == null) return;

    setIsSubmitting(true);
    setError(null);

    try {
      await submitFeedback({
        id: analysisRunId,
        wasHelpful,
        userRemarks: remarks,
        feedbackChoices: selectedChoices.join(";"),
      });
      setSubmitted(true);
    } catch (err) {
      const status = err?.response?.status;
      setError(
        status === 400
          ? "Unable to submit feedback. Please check your response and try again."
          : "Failed to submit feedback. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  if (submitted) {
    return (
      <div
        // Without PENDO_IGNORE_CLASS, Pendo may latch onto this “feedback” banner.
        className={cn(
          PENDO_IGNORE_CLASS,
          "mt-4 rounded-lg border border-green-500/30 bg-green-500/10 px-4 py-3 shadow-sm dark:border-green-500/40 dark:bg-green-500/15"
        )}
        role='status'
        aria-live='polite'>
        <div className='flex items-start gap-3'>
          <CircleCheck
            className='mt-0.5 h-5 w-5 shrink-0 text-green-600 dark:text-green-400'
            aria-hidden
          />
          <div>
            <p className='text-sm font-semibold text-green-900 dark:text-green-100'>
              Thank you for your feedback!
            </p>
            <p className='mt-0.5 text-xs text-green-800/90 dark:text-green-200/90'>
              Your response helps us improve AI shutdown analysis.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const choiceOptions =
    wasHelpful === true
      ? HELPFUL_CHOICES
      : wasHelpful === false
        ? NOT_HELPFUL_CHOICES
        : [];
  const tone = wasHelpful === true ? "helpful" : "not-helpful";

  return (
    <div
      className={cn(
        // Without PENDO_IGNORE_CLASS, Pendo latches onto this feedback UI.
        PENDO_IGNORE_CLASS,
        "mt-4 transition-colors",
        wasHelpful === true
          && "rounded-lg border border-green-500/30 bg-green-500/10 px-4 py-4 dark:border-green-500/40 dark:bg-green-500/15",
        wasHelpful === false
          && "rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-4 dark:border-red-500/40 dark:bg-red-500/15",
        wasHelpful === null && "border-t border-border pt-4"
      )}
      onClick={(e) => {
        // stopPropagation: keep Pendo from treating this panel as its Submit feedback target.
        e.stopPropagation();
      }}>
      <p className='text-sm font-medium text-foreground'>
        Was this analysis helpful?
      </p>
      <div className='mt-3 flex flex-wrap gap-2'>
        <Button
          type='button'
          size='sm'
          variant='outline'
          disabled={isSubmitting}
          onClick={(e) => {
            e.stopPropagation(); // Pendo latches onto these feedback buttons; make sure it can't.
            selectHelpfulness(true);
          }}
          className={cn(
            // Without PENDO_IGNORE_CLASS, Pendo latches onto this feedback UI.
            PENDO_IGNORE_CLASS,
            wasHelpful === true
              ? "border-green-500 bg-green-500/20 text-green-800 hover:bg-green-500/20 hover:text-green-800 dark:text-green-200 dark:hover:text-green-200"
              : "border-border bg-card text-foreground hover:bg-green-500/10 hover:text-foreground"
          )}>
          <ThumbsUp aria-hidden />
          Helpful
        </Button>
        <Button
          type='button'
          size='sm'
          variant='outline'
          disabled={isSubmitting}
          onClick={(e) => {
            // Pendo latches onto these feedback buttons; make sure it can't.
            e.stopPropagation();
            selectHelpfulness(false);
          }}
          className={cn(
            // Without PENDO_IGNORE_CLASS, Pendo latches onto this feedback UI.
            PENDO_IGNORE_CLASS,
            wasHelpful === false
              ? "border-red-500 bg-red-500/20 text-red-800 hover:bg-red-500/20 hover:text-red-800 dark:text-red-200 dark:hover:text-red-200"
              : "border-border bg-card text-foreground hover:bg-red-500/10 hover:text-foreground"
          )}>
          <ThumbsDown aria-hidden />
          Not Helpful
        </Button>
      </div>
      {wasHelpful != null ? (
        <div className='mt-4 space-y-4'>
          <div role='group' aria-labelledby={choicesGroupId}>
            <p
              id={choicesGroupId}
              className='text-xs font-medium text-foreground'>
              What stood out? Select any that apply.
            </p>
            <div className='mt-2 flex flex-wrap gap-2'>
              {choiceOptions.map((choice) => (
                <FeedbackChoiceBubble
                  key={choice}
                  label={choice}
                  tone={tone}
                  selected={selectedChoices.includes(choice)}
                  disabled={isSubmitting}
                  onToggle={() => toggleChoice(choice)}
                />
              ))}
            </div>
          </div>

          <div>
            <label
              htmlFor={remarksId}
              className='text-xs font-medium text-foreground'>
              Additional comments (optional)
            </label>
            <textarea
              id={remarksId}
              value={remarks}
              maxLength={MAX_REMARKS_LENGTH}
              disabled={isSubmitting}
              rows={3}
              placeholder='Tell us more…'
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => setRemarks(e.target.value)}
              className={cn(
                // Without PENDO_IGNORE_CLASS, Pendo latches onto this feedback UI.
                PENDO_IGNORE_CLASS,
                "mt-1.5 block w-full resize-y rounded-md border bg-card px-3 py-2",
                "text-sm font-light leading-6 text-foreground",
                "transition-all duration-150 ease-in-out",
                "focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-offset-background",
                wasHelpful
                  ? "border-green-500/40 focus:border-green-500 focus:ring-green-500/40"
                  : "border-red-500/40 focus:border-red-500 focus:ring-red-500/40",
                "disabled:cursor-not-allowed disabled:bg-muted disabled:opacity-50",
                "placeholder:text-muted-foreground"
              )}
            />
            <p className='mt-1 text-right text-xs text-muted-foreground'>
              {remarks.length}/{MAX_REMARKS_LENGTH}
            </p>
          </div>

          <div className='flex flex-wrap items-center gap-3'>
            <Button
              type='button'
              size='sm'
              disabled={isSubmitting}
              onClick={(e) => {
                e.stopPropagation();
                handleSubmit();
              }}
              className={cn(
                // Without PENDO_IGNORE_CLASS, Pendo latches onto this feedback UI.
                PENDO_IGNORE_CLASS,
                "border-0 text-white shadow-none",
                wasHelpful
                  ? "bg-green-700 hover:bg-green-800 dark:bg-green-600 dark:hover:bg-green-500"
                  : "bg-red-700 hover:bg-red-800 dark:bg-red-600 dark:hover:bg-red-500"
              )}>
              {isSubmitting ? (
                <span className='inline-flex items-center gap-2'>
                  <LoadingSpinner className='h-3.5 w-3.5 border-white border-b-transparent' />
                  Submitting…
                </span>
              ) : (
                "Send Feedback"
              )}
            </Button>
          </div>
        </div>
      ) : null}
      {error ? (
        <p className='mt-3 text-sm text-red-600 dark:text-red-400' role='alert'>
          {error}
        </p>
      ) : null}
    </div>
  );
}
